import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import { type DonationForm, EFormState } from '@feathr/blackbox';
import { ContextMenu, toast } from '@feathr/components';
import DonationFormDeleteModal from '@feathr/extender/App/FundraisePage/DonationFormDeleteModal';
import DonationFormRenameModal from '@feathr/extender/App/FundraisePage/DonationFormRenameModal';
import { useDonationFormModals } from '@feathr/extender/hooks';
import { useLocalUrl, useStore } from '@feathr/extender/state';
import { errorMessage, getIconForAction } from '@feathr/hooks';

export interface IDonationFormRowActionsProps {
  form: DonationForm;
}

function DonationFormRowActions({ form }: Readonly<IDonationFormRowActionsProps>): JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const localUrl = useLocalUrl();
  const { DonationForms } = useStore();
  const { modalHandlers, modalStates } = useDonationFormModals();

  async function handleDuplicate(): Promise<void> {
    try {
      const newForm = await form.clone({ state: EFormState.Draft });
      DonationForms.refreshApiCache();
      toast(
        {
          title: t('Duplicated!'),
          description: t('Open your copy to get started'),
          action: {
            label: t('View form'),
            onClick: () => history.push(localUrl(newForm.getItemUrl('edit'))),
          },
        },
        { type: 'success' },
      );
    } catch (error) {
      toast(
        t("Couldn't duplicate donation form. {{- message}}", {
          message: errorMessage(error, t),
        }),
        { type: 'error' },
      );
    }
  }

  return (
    <>
      <ContextMenu buttonType={'icon'} position={'left-start'}>
        <ContextMenu.Item
          name={'view-donation-form'}
          onClick={() => history.push(localUrl(form.getItemUrl('edit')))}
          prefix={getIconForAction('edit')}
        >
          {t('Edit')}
        </ContextMenu.Item>
        <ContextMenu.Item
          name={'rename-donation-form'}
          onClick={modalHandlers.openRenameModal}
          prefix={getIconForAction('rename')}
        >
          {t('Rename')}
        </ContextMenu.Item>
        <ContextMenu.Item
          name={'duplicate-donation-form'}
          onClick={handleDuplicate}
          prefix={getIconForAction('duplicate')}
        >
          {t('Duplicate')}
        </ContextMenu.Item>
        <ContextMenu.Item
          name={'delete-donation-form'}
          onClick={modalHandlers.openDeleteModal}
          prefix={getIconForAction('delete')}
          theme={'danger'}
        >
          {t('Delete')}
        </ContextMenu.Item>
      </ContextMenu>
      <DonationFormRenameModal
        form={form}
        onClose={modalHandlers.closeRenameModal}
        opened={modalStates.isRenameModalOpen}
      />
      <DonationFormDeleteModal
        form={form}
        onClose={modalHandlers.closeDeleteModal}
        opened={modalStates.isDeleteModalOpen}
      />
    </>
  );
}

export default observer(DonationFormRowActions);
